import { PushSubscriptionJson } from './userAccessApi';
import { getAppServiceWorkerRegistration } from './serviceWorker';

const ensureSupported = () => {
  if (!('Notification' in window)) {
    throw new Error('Tento prehliadač nepodporuje push notifikácie.');
  }
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service worker nie je v tomto prehliadači dostupný.');
  }
  if (!('PushManager' in window)) {
    throw new Error('Web Push nie je v tomto prehliadači dostupný.');
  }
};

const toUint8Array = (base64Url: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const normalized = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};

const toSubscriptionJson = (subscription: PushSubscription): PushSubscriptionJson => {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint || '',
    expirationTime: json.expirationTime ?? null,
    keys: {
      p256dh: json.keys?.p256dh || '',
      auth: json.keys?.auth || '',
    },
  };
};

const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration> => {
  return getAppServiceWorkerRegistration();
};

const getServiceWorkerRegistrations = async (): Promise<ServiceWorkerRegistration[]> => {
  const registrations = await navigator.serviceWorker.getRegistrations();
  const appRegistration = await getServiceWorkerRegistration();

  return registrations.some((registration) => registration.scope === appRegistration.scope)
    ? [...registrations]
    : [appRegistration, ...registrations];
};

const getExistingSubscriptions = async (): Promise<PushSubscription[]> => {
  const registrations = await getServiceWorkerRegistrations();
  const subscriptions: PushSubscription[] = [];
  const seenEndpoints = new Set<string>();

  for (const registration of registrations) {
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      continue;
    }

    if (seenEndpoints.has(subscription.endpoint)) {
      continue;
    }

    seenEndpoints.add(subscription.endpoint);
    subscriptions.push(subscription);
  }

  return subscriptions;
};

export const getBrowserPushSubscription = async (): Promise<PushSubscriptionJson | null> => {
  ensureSupported();
  const subscriptions = await getExistingSubscriptions();
  return subscriptions.length > 0 ? toSubscriptionJson(subscriptions[0]) : null;
};

export const registerBrowserPush = async (publicKey: string): Promise<PushSubscriptionJson> => {
  ensureSupported();

  if (!publicKey || !publicKey.trim()) {
    throw new Error('Web Push VAPID public key nie je nakonfigurovaný.');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new Error('Povolenie notifikácií nebolo udelené.');
  }

  const existingSubscriptions = await getExistingSubscriptions();
  if (existingSubscriptions.length > 0) {
    return toSubscriptionJson(existingSubscriptions[0]);
  }

  const registration = await getServiceWorkerRegistration();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: toUint8Array(publicKey.trim()),
  });

  return toSubscriptionJson(subscription);
};

export const unregisterBrowserPush = async (): Promise<PushSubscriptionJson[]> => {
  ensureSupported();

  const subscriptions = await getExistingSubscriptions();
  await Promise.all(subscriptions.map((subscription) => subscription.unsubscribe().catch(() => false)));
  return subscriptions.map((subscription) => toSubscriptionJson(subscription));
};
